/**
 * 이상한 방 - 리빌 연출 (라운드의 클라이맥스)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 5절(웃김 설계 포인트 6번) ·
 *            `이상한방-상태모델-v0.1.md` 원칙 2(1절) · 3.1절(가려진 위치는 허용)
 *
 * ## 이 파일이 만드는 것은 정보가 아니라 반응이다
 *
 * 컨셉시트 5절은 리빌을 **"저걸 못 봤다고???" - 클립 1순위 재료**로 지목했다.
 * 즉 이 모듈의 성공 기준은 "미발견 목록을 정확히 나열했는가"가 아니라
 * **마지막 한 건에서 소리가 나오는가**다. 그래서 세 가지를 설계에 박았다.
 *
 *  1. **미발견을 먼저, 그 안에서 쉬운 것 → 어려운 것 순.** 수색이 끝난 직후가 긴장의 최고점이고,
 *     이미 찾은 것부터 훑으면 그 긴장이 먼저 새어 나간다. 미발견 안에서는 뒤로 갈수록 교묘해져야
 *     마지막이 가장 크게 터진다. 발견분은 맨 뒤에서 **빠르게** 훑어 점수 집계처럼 소비된다.
 *  2. **원본↔변경본 깜빡 교차.** 수색 중에는 이것이 치트라 쿨타임 30초로 막아 뒀지만
 *     (`tuning.ts`의 `originCooldownSeconds` 주석), 수색이 끝난 리빌에서는 정반대다 -
 *     깜빡비교는 인간의 시각이 diff를 즉시 떠 주는 유일한 방법이고, 여기서는 그게 정답이다.
 *  3. **가려졌으면 각도를 돌린다.** 안 보이는 리빌은 리빌이 아니다. 3.1절이 허용한 "가려진 위치"는
 *     자유 카메라로 접근 가능하다는 전제 위에서만 정당하므로, 연출도 그 전제를 지켜야 한다.
 *
 * ## 가려짐을 왜 레이캐스트가 아니라 그리드로 재는가
 *
 * `buildRevealSequence`는 순수 함수다 - `THREE.Scene`도, 렌더된 메시도 받지 않는다.
 * 대신 아이소 카메라의 시선이 **정확히 그리드 대각선**이라는 사실을 쓴다:
 * `isoOffsetDirection`이 돌려주는 벡터는 x·y·z 성분의 크기가 모두 같으므로
 * (±0.577, +0.577, ±0.577), 대상 셀에서 (±1, +1, ±1)씩 걸어가면 그게 곧 시선 위의 셀들이다.
 * 셀이 정육면체라 근사가 아니라 등가다. 덕분에 가려짐 판정이 씬 없이 테스트된다.
 *
 * 벽은 차단물로 세지 않는다. 컷어웨이(`render/cutaway.ts`)가 천장과 카메라 쪽 외벽을 걷어내고
 * 내벽은 허리에서 자르므로, 벽을 넣으면 벽에 붙은 가구가 **네 방향 모두 똑같이 막힘**으로 잡혀
 * 방향 선택 신호가 통째로 사라진다. 실제 시야를 막는 것은 큰 가구다.
 *
 * ## 상태 변경 규약
 *
 * 재생기는 `IsoCamera`만 제자리 조작한다(11.3절이 "클라이언트가 해도 되는 것"으로 규정한 영역).
 * `GameState`는 읽기만 한다. 시간은 프레임 델타로만 흐르고 `Date.now()`를 쓰지 않는다 -
 * 연출은 시드 결정론의 대상이 아니지만, 상태 생성 경로에 시계가 섞이는 사고를 아예 막는다.
 *
 * ## 재생기는 `iso.update(dt)`를 부르지 않는다
 *
 * 프레임 루프(`main.ts`)가 이미 카메라를 진행시키고 있다. 여기서 또 부르면 보간이 두 배 속도로
 * 돌아 연출 길이가 절반이 된다. 이 모듈은 "어디를 보라"고 지시만 한다.
 */

import * as THREE from 'three'

import { cellKey, cellToWorld, occupiedCells, rotatedFootprint } from '../core/grid'
import { findRoom, roomAt, type FloorPlan } from '../core/floorPlan'
import { clamp01, smoothstep } from '../core/math'
import { getObject, listObjects, type Catalog } from '../core/room'
import {
  CELL_SIZE_METERS,
  type CatalogEntry,
  type Cell3,
  type ObjectInstance,
  type PlayerId,
  type RoomState,
  type Rot,
} from '../core/types'
import { t } from '../i18n'
import { itemName } from '../i18n/itemName'
import { zoneName } from '../i18n/zoneName'
import type { IsoCamera, IsoDirection } from '../input/isoCamera'
import {
  ISO_AZIMUTH_BASE_RADIANS,
  ISO_DIRECTION_COUNT,
  ISO_QUARTER_TURN_RADIANS,
  isoOffsetDirection,
} from '../render/scene'
import type { Change, ChangeVerb } from './changeGen'
import type { GameState } from './gameState'
import type { FoundLike, RevealFinale } from './revealOrder'
import { orderChanges } from './revealOrder'
import { TUNING } from './tuning'

/* --- 연출 손잡이 ---------------------------------------------------------------- */

/**
 * 리빌의 **템포**. 라운드 흐름 수치(`tuning.ts`)와 겹치지 않게 나눈 경계다 -
 * 스텝 길이의 원본은 `TUNING.revealSecondsPerChange`이고, 여기 있는 것은
 * "한 스텝 2.5초를 어떻게 쪼개 쓰는가"뿐이다.
 */
const REVEAL_PACING = {
  /** 스텝 진입 후 카메라가 자리를 잡는 시간(초). `isoCamera`의 focusOn 보간(0.45초)에 맞췄다. */
  approachSeconds: 0.5,
  /** 한 스텝에서 접근에 쓸 수 있는 최대 비율. 짧은 스텝이 접근만 하다 끝나는 것을 막는다. */
  approachMaxFraction: 0.45,
  /**
   * 깜빡 교차 한 장의 노출 시간(초).
   * 더 짧으면 잔상이 겹쳐 "무엇이" 달라졌는지가 아니라 "뭔가 번쩍했다"만 남고,
   * 더 길면 두 장이 별개의 그림으로 읽혀 diff가 눈에 안 떠오른다.
   */
  blinkIntervalSeconds: 0.35,
  /** 이미 찾은 변화의 스텝 길이 배수. 확인 사살이므로 짧게 훑는다. */
  foundStepScale: 0.4,
  /**
   * 스텝 길이 하한(초). 튜닝을 0으로 덮어써도 진행 루프가 무한히 돌지 않게 하는 안전장치이기도 하다.
   */
  minStepSeconds: 0.08,
} as const

/**
 * 리빌의 **화각**. 대상 크기에 맞춰 줌을 고르는 표다.
 *
 * 너무 당기면 "어디였는지"가 사라져서 못 알아보고(=반응이 안 나온다), 너무 빠지면 변화가 안 보인다.
 * 화면 세로에 3m 남짓이 담기는 구간이 "물건 + 그 주변 맥락"이 같이 읽히는 지점이다.
 * 전부 플레이테스트 대상 값이다(컨셉시트 7절).
 */
const REVEAL_FRAMING = {
  /** 이 크기(m) 이하는 소품. 머그·책 같은 1셀짜리. */
  smallSpanMeters: 0.5,
  mediumSpanMeters: 1.2,
  largeSpanMeters: 2.5,
  /** 정규화 줌(0 = 방 전체, 1 = 화면 세로 2m). */
  zoomSmall: 0.82,
  zoomMedium: 0.68,
  zoomLarge: 0.52,
  /** 멀리 이동한 변화 - 출발지와 도착지를 한 화면에 담아야 "옮겨졌다"가 읽힌다. */
  zoomWide: 0.4,
} as const

/**
 * 방향을 바꾸는 데 필요한 이득(차단 셀 수).
 *
 * 매 스텝 최적 각도를 새로 고르면 12번 중 여러 번 화면이 90°씩 돌아 방향 감각이 통째로 무너진다.
 * 지금 각도가 "그럭저럭 보이는" 수준이면 유지한다 - 회전은 진짜 가려졌을 때만 쓴다.
 */
const DIRECTION_STICKINESS = 1

/** 시선 탐침의 최대 진행 셀 수. 방 높이가 곧 상한이지만 루프를 무조건 유한하게 묶어 둔다. */
const MAX_PROBE_STEPS = 128

const HALF = 0.5

/* --- 타입 ----------------------------------------------------------------------- */

/**
 * 지금 화면에 그려야 하는 방.
 * `'mutated'`는 술래가 수색하던 방, `'original'`은 외우기 페이즈의 방이다.
 */
export type RevealView = 'mutated' | 'original'

/**
 * 리빌 한 스텝. 계약의 `{ change, focusWorld, label }`에 연출이 필요로 하는 세 가지를 더했다.
 *
 * `direction`·`zoom`을 **데이터로 빼 둔 이유**는 순수 함수 쪽에서 테스트하기 위해서다.
 * 재생기 안에서 계산하면 "가려진 변화에 올바른 각도를 골랐는가"를 프레임을 돌려야만 확인할 수 있다.
 */
export type RevealStep = Readonly<{
  change: Change
  /** 카메라가 볼 월드 좌표. 이동은 출발·도착의 중간점이라 양쪽이 한 화면에 들어온다. */
  focusWorld: THREE.Vector3
  /**
   * 지금 언어의 한 줄. 예: "거실의 소파가 옮겨졌다" / "The sofa moved across the Living room".
   * 문장은 사전이 만든다(`labelFor`) - 여기서 조립하지 마라.
   */
  label: string
  /** 이 좌표를 가장 잘 보여주는 4방향 인덱스. */
  direction: IsoDirection
  /** 정규화 줌(0 = 방 전체, 1 = 최대 확대). */
  zoom: number
  /** 수색에서 이미 찾은 변화인가. true면 짧게 훑는다. */
  found: boolean
  /**
   * 누가 찾았는가(여럿이, 로드맵 M5). **선택 필드**다 - 싱글에는 "누가"가 없어 스텝에 이 자리가 비고, 비어 있으면
   * `label`을 포함한 나머지 전부가 이 필드가 생기기 전과 **같은 문자열·같은 값**이다(`reveal.test.ts`가 지킨다).
   * 문장(`label`)에 이름을 섞지 않는 이유는 이름표가 문장이 아니라 **사람**이기 때문이다 - 찾은 사람의 이름은
   * 사전을 거치지 않고(`juice.HitJuice.by`와 같은 결정) 리빌 화면의 별도 자리(`ui/revealParty.ts`)에 찍힌다.
   */
  foundBy?: PlayerId
  /** 선택된 방향에서 시선을 막는 셀 수. 0이면 완전히 열려 있다(계측·테스트용). */
  blockedCells: number
}>

/**
 * 시퀀스를 세울 때 고를 수 있는 것 둘. **둘 다 없으면 싱글이다** - 마지막 자리는 가장 교묘한 것(`'hardest'`)이고
 * 스텝에 `foundBy`가 없다. 여럿이(`game/matchSessionReveal.ts`)는 `finale: 'easiest'`("저걸 아무도 못 봤다고???" -
 * `revealOrder.ts` 머리말)와 발견 목록의 `by`를 넘긴다. 카메라·화각·라벨은 어느 쪽이든 같은 코드다 - 순서와 이름표만 다르다.
 */
export type RevealSequenceOptions = Readonly<{
  /** 미발견 안의 방향. 기본 `'hardest'`(싱글). */
  finale?: RevealFinale
  /** 변화 id → 찾은 사람. 있는 것만 스텝의 `foundBy`가 된다. */
  foundBy?: ReadonlyMap<string, PlayerId>
}>

/**
 * 스텝 리스너.
 *
 * **스텝 진입 때 한 번, 그리고 깜빡 교차로 화면이 바뀔 때마다** 불린다.
 * 두 번째 인자가 지금 그려야 할 방이므로, 호출부는 이렇게 쓰면 된다:
 *
 * ```ts
 * (step, view) => { hud.setRevealLabel(step.label); showRoom(view === 'original' ? original : mutated) }
 * ```
 *
 * 인자를 하나만 받는 함수(`(step) => ...`)를 그대로 넘겨도 타입·동작 모두 문제없다.
 * 그 경우 카메라 연출과 라벨만 살아 있고 깜빡 교차만 빠진다.
 */
export type RevealStepListener = (step: RevealStep, view: RevealView, index: number) => void

export type RevealPlayerOptions = Readonly<{
  /** 미발견 한 건에 쓰는 시간(초). 기본값은 `TUNING.revealSecondsPerChange`. */
  secondsPerStep?: number
  /**
   * 전체 상한(초). 주면 모든 스텝을 같은 비율로 줄여 이 안에 맞춘다(늘리지는 않는다).
   *
   * 리빌 페이즈의 길이는 `gameState.phaseDurationMs`가 **미발견 수 × 스텝 시간**으로 잡는다.
   * 발견분 훑기까지 하는 이 재생기의 자연 길이는 그보다 길 수 있으므로,
   * 페이즈 타이머에 맞추고 싶으면 그 값을 여기 넘긴다.
   */
  budgetSeconds?: number
}>

export type RevealPlayer = {
  /** 프레임 델타(초)를 넣는다. `iso.update`는 호출부가 따로 부른다. */
  update(dt: number): void
  /** 남은 연출을 버리고 즉시 완료한다(`onDone` 1회). */
  skip(): void
  /** 수명 종료. 이후 어떤 콜백도 불리지 않는다. */
  dispose(): void
  /** 이 재생기가 실제로 쓸 총 시간(초). HUD 진행바가 쓴다. */
  readonly totalSeconds: number
}

/* --- 순서 - 무엇을 언제 보여줄 것인가 --------------------------------------------- */

/*
 * 순서 규칙(미발견 먼저 · 놀람 순위 · id 타이브레이크)은 `revealOrder.ts`로 갔다.
 * 이 파일은 `three`와 `i18n`을 알아 서버가 못 쓰는데, 순서는 규칙이고 카메라는 연출이라
 * 매치 호스트가 리빌 스텝 인덱스를 쥐려면 순서를 씬 없이 계산할 수 있어야 한다.
 * 싱글의 마지막 자리는 가장 교묘한 것(`'hardest'`)이다 - "저걸 못 봤다고???". 여럿이는 `RevealSequenceOptions.finale`로
 * `'easiest'`를 고른다 - 이 파일은 그 값을 `orderChanges`에 넘길 뿐이고 순서의 뜻은 `revealOrder.ts`가 갖는다.
 */

/* --- 라벨 - 문장은 사전이 만든다 -------------------------------------------------- */

/**
 * 리빌 한 줄. 동사와 구획 유무로 사전 키를 조립한다
 * (`reveal.move.inRoom` / `reveal.move.plain`).
 *
 * ## 왜 이어붙이지 않는가
 *
 * 직전까지 이 자리는 `'거실의 ' + name + subjectParticle(name) + ' 사라졌다'`였고,
 * 받침 판정(`hasFinalConsonant`)도 이 파일이 들고 있었다. 둘 다 사전으로 넘겼다.
 *
 * 이어붙이기는 **한국어 안에서만** 맞는 코드다. 영어는 "The sofa vanished from the living room"
 * 이라 구획이 문장 뒤로 가고, 어순이 뒤집히는 순간 이 함수에는 손쓸 방법이 없다.
 * 조사도 마찬가지다 - "소파가"와 "머그컵이"를 가르는 규칙은 문구 엔진의 `{item:이/가}` 표기가
 * 이미 알고 있고(`i18n/index.ts`), 같은 규칙을 두 곳에 두면 반드시 한쪽만 고쳐진다.
 *
 * 돌아감과 색 바뀜에 조사 표기가 없는 이유는 주어가 물건이 아니라 **속성**이기 때문이다
 * ("소파 방향이 바뀌었다"). "소파가 돌아갔다"는 뜻이 갈리고 무엇이 달라졌는지도 흐려진다.
 */
function labelFor(verb: ChangeVerb, item: string, room: string | undefined): string {
  return room === undefined
    ? t(`reveal.${verb}.plain`, { item })
    : t(`reveal.${verb}.inRoom`, { item, room })
}

/* --- 기하 - 카메라가 볼 좌표와 화각 ----------------------------------------------- */

/**
 * 점유 박스의 **중심** 월드 좌표.
 *
 * `cell`은 최소 코너 규약이므로(3절) 중심은 (size − 1)/2 셀만큼 떨어져 있다.
 * 이 보정을 빼먹으면 소파처럼 큰 물건에서 카메라가 모서리를 비추고 정작 물건은 화면 밖으로 밀린다.
 */
function centerWorld(cell: Cell3, entry: CatalogEntry, rot: Rot): THREE.Vector3 {
  const size = rotatedFootprint(entry.footprint, rot)
  const base = cellToWorld(cell)
  return new THREE.Vector3(
    base.x + (size.x - 1) * HALF * CELL_SIZE_METERS,
    base.y + (size.y - 1) * HALF * CELL_SIZE_METERS,
    base.z + (size.z - 1) * HALF * CELL_SIZE_METERS,
  )
}

/** 대상이 화면에서 차지해야 하는 크기(m). 물건의 최대 변과 이동 거리 중 큰 쪽이다. */
function spanMeters(entry: CatalogEntry, rot: Rot, travel: number): number {
  const size = rotatedFootprint(entry.footprint, rot)
  const longestSide = Math.max(size.x, size.y, size.z) * CELL_SIZE_METERS
  return Math.max(longestSide, travel)
}

function zoomForSpan(span: number): number {
  if (span <= REVEAL_FRAMING.smallSpanMeters) return REVEAL_FRAMING.zoomSmall
  if (span <= REVEAL_FRAMING.mediumSpanMeters) return REVEAL_FRAMING.zoomMedium
  if (span <= REVEAL_FRAMING.largeSpanMeters) return REVEAL_FRAMING.zoomLarge
  return REVEAL_FRAMING.zoomWide
}

/* --- 가려짐 - 어느 각도에서 보이는가 ---------------------------------------------- */

/**
 * dir별 시선 방향의 x·z 부호.
 *
 * 표를 손으로 적지 않고 `scene.ts`의 카메라 함수에서 뽑는다. 손으로 적으면 카메라 회전 방향이
 * 바뀌었을 때 **가려짐 판정만 조용히 반대로 돈다** - 화면은 멀쩡한데 리빌이 벽 뒤를 비추는,
 * 원인 추적이 불가능한 종류의 고장이다.
 */
const PROBE_SIGNS: readonly Readonly<{ x: number; z: number }>[] = Array.from(
  { length: ISO_DIRECTION_COUNT },
  (_unused, dir) => {
    const offset = isoOffsetDirection(ISO_AZIMUTH_BASE_RADIANS + dir * ISO_QUARTER_TURN_RADIANS)
    return { x: Math.sign(offset.x), z: Math.sign(offset.z) }
  },
)

/**
 * 시야를 막을 수 있는 셀 전부와 그 바운딩.
 * 바운딩을 같이 들고 다니는 이유는 탐침을 조기 종료시키기 위해서다 - 시선은 위로 올라가므로
 * 가장 높은 차단물을 지나면 그 뒤로는 아무것도 막을 수 없다.
 */
type Blockers = Readonly<{
  cells: ReadonlySet<string>
  maxY: number
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}>

const EMPTY_BLOCKERS: Blockers = {
  cells: new Set<string>(),
  // 첫 탐침 셀(y ≥ 1)에서 즉시 종료되도록 음수로 둔다.
  maxY: -1,
  minX: 0,
  maxX: -1,
  minZ: 0,
  maxZ: -1,
}

function buildBlockers(states: readonly RoomState[], catalog: Catalog): Blockers {
  const cells = new Set<string>()
  let maxY = -1
  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let minZ = Number.POSITIVE_INFINITY
  let maxZ = Number.NEGATIVE_INFINITY

  for (const state of states) {
    for (const object of listObjects(state)) {
      const entry = catalog.get(object.catalogId)
      if (entry === undefined) continue

      for (const cell of occupiedCells(object.cell, entry, object.rot)) {
        cells.add(cellKey(cell))
        if (cell.y > maxY) maxY = cell.y
        if (cell.x < minX) minX = cell.x
        if (cell.x > maxX) maxX = cell.x
        if (cell.z < minZ) minZ = cell.z
        if (cell.z > maxZ) maxZ = cell.z
      }
    }
  }

  if (cells.size === 0) return EMPTY_BLOCKERS
  return { cells, maxY, minX, maxX, minZ, maxZ }
}

/**
 * 대상 셀에서 카메라 쪽으로 걸어가며 막힌 셀 수를 센다.
 *
 * 파일 상단에 적은 대로 한 걸음은 (±1, +1, ±1)이고, 이것이 아이소 시선과 정확히 일치한다.
 * 자기 자신(`own`)은 세지 않는다 - 소파의 뒷면이 소파를 가린다고 판정하면 모든 방향이 똑같이 막힌다.
 */
function countBlocked(
  origin: Cell3,
  sign: Readonly<{ x: number; z: number }>,
  blockers: Blockers,
  own: ReadonlySet<string>,
): number {
  let blocked = 0
  let x = origin.x
  let y = origin.y
  let z = origin.z

  for (let step = 0; step < MAX_PROBE_STEPS; step += 1) {
    x += sign.x
    y += 1
    z += sign.z

    if (y > blockers.maxY) break
    if (x < blockers.minX || x > blockers.maxX) break
    if (z < blockers.minZ || z > blockers.maxZ) break

    const key = cellKey({ x, y, z })
    if (own.has(key)) continue
    if (blockers.cells.has(key)) blocked += 1
  }

  return blocked
}

/**
 * 한 방향에서 대상이 얼마나 가려지는가.
 *
 * 탐침을 **중심 셀과 윗면 셀 두 곳**에서 쏘고 작은 쪽을 택한다. 하나라도 뚫려 있으면
 * 물건의 일부는 보이기 때문이다 - 책상 밑 머그컵은 중심에서는 막히지만 윗면에서는 뚫린다.
 */
function occlusionScore(
  cell: Cell3,
  entry: CatalogEntry,
  rot: Rot,
  sign: Readonly<{ x: number; z: number }>,
  blockers: Blockers,
  own: ReadonlySet<string>,
): number {
  const size = rotatedFootprint(entry.footprint, rot)
  const centerCell: Cell3 = {
    x: cell.x + Math.floor((size.x - 1) * HALF),
    y: cell.y + Math.floor((size.y - 1) * HALF),
    z: cell.z + Math.floor((size.z - 1) * HALF),
  }
  const topCell: Cell3 = { x: centerCell.x, y: cell.y + size.y - 1, z: centerCell.z }

  return Math.min(
    countBlocked(centerCell, sign, blockers, own),
    countBlocked(topCell, sign, blockers, own),
  )
}

/**
 * 여러 지점을 **동시에** 봐야 할 때의 가려짐. 이동은 출발지와 도착지가 둘 다 보여야 하므로
 * 나쁜 쪽(최댓값)을 그 방향의 점수로 삼는다. 한쪽만 보이는 각도로 "옮겨졌다"를 보여 줄 수는 없다.
 */
function directionScore(
  cells: readonly Cell3[],
  entry: CatalogEntry,
  rot: Rot,
  sign: Readonly<{ x: number; z: number }>,
  blockers: Blockers,
  own: ReadonlySet<string>,
): number {
  let worst = 0
  for (const cell of cells) {
    const score = occlusionScore(cell, entry, rot, sign, blockers, own)
    if (score > worst) worst = score
  }
  return worst
}

/**
 * 네 방향 중 가장 잘 보이는 각도를 고른다.
 *
 * 직전 스텝의 각도가 `DIRECTION_STICKINESS` 이내로 비슷하면 그대로 둔다 -
 * 12번 연속으로 화면이 도는 리빌은 연출이 아니라 멀미다.
 */
function chooseDirection(
  cells: readonly Cell3[],
  entry: CatalogEntry,
  rot: Rot,
  blockers: Blockers,
  own: ReadonlySet<string>,
  previous: IsoDirection | null,
): { direction: IsoDirection; blockedCells: number } {
  let best: IsoDirection = 0
  let bestScore = Number.POSITIVE_INFINITY

  for (let dir = 0; dir < ISO_DIRECTION_COUNT; dir += 1) {
    const sign = PROBE_SIGNS[dir]
    if (sign === undefined) continue

    const score = directionScore(cells, entry, rot, sign, blockers, own)
    if (score >= bestScore) continue
    best = dir as IsoDirection
    bestScore = score
  }

  if (previous === null) return { direction: best, blockedCells: bestScore }

  const previousSign = PROBE_SIGNS[previous]
  if (previousSign === undefined) return { direction: best, blockedCells: bestScore }

  const previousScore = directionScore(cells, entry, rot, previousSign, blockers, own)
  if (previousScore <= bestScore + DIRECTION_STICKINESS) {
    return { direction: previous, blockedCells: previousScore }
  }

  return { direction: best, blockedCells: bestScore }
}

/* --- 시퀀스 조립 ----------------------------------------------------------------- */

function sameCell(a: Cell3, b: Cell3): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z
}

/**
 * 카탈로그에 없는 소품의 대체 **규격**. 리빌이 예외로 죽는 것보다 무엇이라도 비추는 편이 낫다.
 *
 * `name`은 화면에 나가지 않는다 - 표시용 이름은 `t('reveal.unknownItem')`이 만든다
 * (`buildRevealSequence`의 `itemName`). 여기에 한국어를 적어 두면 언어를 바꿔도 그 한 줄만
 * 한국어로 남고, 그런 구멍은 눈에 잘 띄지도 않는다. 대신 눈에 띄는 값을 넣어 둔다.
 */
const FALLBACK_ENTRY: CatalogEntry = {
  id: 'unknown',
  name: 'unknown',
  footprint: { x: 1, y: 1, z: 1 },
  rotSymmetry: 4,
  defaultColorId: 0,
}

/**
 * 리빌 순서를 세우는 데 필요한 것 전부 - 두 방 · 정답표 · 발견 목록. `GameState`가 이 모양을 그대로 만족하고,
 * 매치 클라이언트는 `rooms` 메시지로 받은 두 방과 뷰의 `changes`(리빌부터 온다) · `found`(줄마다 `by`가 있다)를
 * 이 모양으로 넘긴다. `GameState`를 통째로 요구하면 매치가 가짜 상태를 지어내야 하고, 그 가짜가 곧 두 번째 사본이다.
 * `found`는 `revealOrder.FoundLike`(`changeId` · `foundAtMs`)까지만 읽는다.
 */
export type RevealSource = Readonly<
  Pick<GameState, 'original' | 'mutated' | 'changes'> & { found: readonly FoundLike[] }
>

/** 변화 대상의 인스턴스. 제거는 원본에만, 추가는 변경본에만 있다. */
function resolveInstance(state: RevealSource, change: Change): ObjectInstance | undefined {
  return (
    getObject(state.mutated, change.targetId) ?? getObject(state.original, change.targetId)
  )
}

/** 두 상태에서 대상이 점유하는 셀 전부. 자기 가림을 제외하는 데 쓴다. */
function ownCells(state: RevealSource, change: Change, catalog: Catalog): ReadonlySet<string> {
  const keys = new Set<string>()

  for (const room of [state.original, state.mutated]) {
    const instance = getObject(room, change.targetId)
    if (instance === undefined) continue
    const entry = catalog.get(instance.catalogId) ?? FALLBACK_ENTRY
    for (const cell of occupiedCells(instance.cell, entry, instance.rot)) keys.add(cellKey(cell))
  }

  return keys
}

/**
 * 구획 이름. `zoneId`가 있으면 그것을, 없으면 좌표로 되짚는다. 평면도가 없으면 생략한다.
 *
 * **사전이 유일한 출처다.** 평면도의 `Room.name`은 에디터·디버그용 키 문자열이지 화면 문구가 아니므로
 * (`floorPlanModel.Room` 주석), 사전에 없는 구획은 이름을 **생략한다** - 예전에는 `room.name`으로 떨어졌지만
 * 그러면 화면에 'bedroom-guest의 소파가 옮겨졌다'가 뜬다. 이름 한 조각이 빠진 문장이 번역되지 않은 슬러그보다 낫다.
 * (키가 빠지는 것은 데이터 오류다 - `i18n/index.test.ts`가 모든 평면도의 구획 id에 키가 있는지 본다.)
 *
 * 사전 조회 자체는 `i18n/zoneName.ts`가 한다(M11 - 같은 이름을 다섯 화면이 말하게 되면서 `room.` 접두사를
 * 아는 파일을 하나로 모았다). 여기 남은 것은 "어느 구획인가"(id 또는 좌표)뿐이다.
 */
function zoneNameFor(change: Change, cell: Cell3, plan: FloorPlan | undefined): string | undefined {
  if (plan === undefined) return undefined

  const byId = change.zoneId === undefined ? undefined : findRoom(plan, change.zoneId)
  const room = byId ?? roomAt(plan, cell.x, cell.z)
  return zoneName(room?.id)
}

/**
 * 미발견 변화를 먼저, 발견분을 뒤에 놓은 **공개 순서** 전체를 만든다.
 *
 * 발견분까지 스텝으로 넣는 이유는 리빌이 곧 결과 발표이기 때문이다 -
 * "네가 찾은 것 7개, 못 찾은 것 5개"를 숫자가 아니라 화면으로 보여 주면 점수판이 필요 없다.
 *
 * `plan`은 선택이다. 주면 라벨에 구획 이름이 붙어("거실의 소파가 옮겨졌다") 어디 얘기인지가
 * 한 줄로 읽힌다. 없으면 물건 이름만 나온다.
 */
export function buildRevealSequence(
  state: RevealSource,
  catalog: Catalog,
  plan?: FloorPlan,
  options: RevealSequenceOptions = {},
): readonly RevealStep[] {
  const blockers = buildBlockers([state.original, state.mutated], catalog)
  const steps: RevealStep[] = []
  const warned = new Set<string>()
  let previousDirection: IsoDirection | null = null

  for (const ordered of orderChanges(state.changes, state.found, options.finale ?? 'hardest')) {
    const change = ordered.change
    const instance = resolveInstance(state, change)
    const catalogId = change.catalogId ?? instance?.catalogId
    const found = catalogId === undefined ? undefined : catalog.get(catalogId)
    const entry = found ?? FALLBACK_ENTRY

    // 대체 규격으로 굴러가긴 하지만, 라벨이 "무언가"로 나오는 것은 카탈로그 배선 사고다.
    // 조용히 넘기면 그 판의 리빌이 왜 이상했는지 나중에 알 수 없다.
    if (found === undefined && catalogId !== undefined && !warned.has(catalogId)) {
      warned.add(catalogId)
      console.warn(`[reveal] 카탈로그에 없는 소품 '${catalogId}' - 라벨과 화각이 기본값으로 떨어진다`)
    }

    /*
     * 소품 이름은 **사전이 낸다**(`i18n/itemName.ts`). 예전에는 카탈로그의 `name`을 그대로 썼는데
     * 그 값이 한국어 리터럴이라 영어 문장이 `A new ㄱ자 책상 showed up`이 됐다 - 소품 151종 전부.
     * "문구 파일이 부푸는 것을 피한다"는 옛 근거(GLOSSARY 5절)보다 화면에 한국어가 새는 쪽이 나쁘다.
     * 카탈로그의 `name`은 이제 에디터·디버그용 키 문자열이다.
     */
    const itemLabel = itemName(catalogId) ?? t('reveal.unknownItem')
    const rot: Rot = instance?.rot ?? 0

    // 좌표의 우선순위: 변화가 기록한 before/after → 인스턴스의 현재 셀.
    const before = change.beforeCell ?? instance?.cell
    const after = change.afterCell ?? instance?.cell
    const anchor = before ?? after ?? { x: 0, y: 0, z: 0 }

    const beforeWorld = centerWorld(before ?? anchor, entry, rot)
    const afterWorld = centerWorld(after ?? anchor, entry, rot)
    // 이동은 두 지점의 중간을 본다. 나머지 동사는 두 좌표가 같으므로 중간점이 곧 그 지점이다.
    const focusWorld = beforeWorld.clone().add(afterWorld).multiplyScalar(HALF)
    const travel = beforeWorld.distanceTo(afterWorld)

    // 이동이면 출발지와 도착지가 둘 다 보여야 한다. 나머지 동사는 두 셀이 같다.
    const probeCells =
      after === undefined || sameCell(anchor, after) ? [anchor] : [anchor, after]

    const chosen = chooseDirection(
      probeCells,
      entry,
      rot,
      blockers,
      ownCells(state, change, catalog),
      previousDirection,
    )
    previousDirection = chosen.direction

    // 이름표는 발견분에만 붙는다. 없으면 필드 자체를 남기지 않는다 - 싱글 스텝은 이 필드가 생기기 전과 같은 모양이어야 한다.
    const foundBy = ordered.found ? options.foundBy?.get(change.id) : undefined

    steps.push({
      change,
      focusWorld,
      label: labelFor(change.verb, itemLabel, zoneNameFor(change, anchor, plan)),
      direction: chosen.direction,
      zoom: zoomForSpan(spanMeters(entry, rot, travel)),
      found: ordered.found,
      ...(foundBy === undefined ? {} : { foundBy }),
      blockedCells: chosen.blockedCells,
    })
  }

  return steps
}

/* --- 재생기 --------------------------------------------------------------------- */

/**
 * 스텝 시퀀스를 카메라 연출로 재생한다.
 *
 * 한 스텝의 시간표는 두 구간이다:
 *
 *  1. **접근(약 0.5초)** - `setDirection` + `focusOn`으로 이동하며 줌을 부드럽게 당긴다.
 *     줌을 한 번에 스냅하면 이동 중에 화면이 튀어 어디로 가는지 알 수 없다.
 *  2. **깜빡 교차** - 자리를 잡은 다음부터 `blinkIntervalSeconds`마다 원본↔변경본을 뒤집는다.
 *     **반드시 카메라가 멈춘 뒤여야 한다** - 움직이는 화면 위에서 뒤집으면 카메라 움직임이
 *     변화의 motion transient를 덮어 버려서 깜빡비교의 유일한 장점이 사라진다.
 *
 * 스텝은 항상 `'mutated'`(술래가 보던 방)로 시작하고, 시퀀스 끝에서도 그 상태로 돌려놓는다.
 * 리빌이 끝난 화면에 원본이 남아 있으면 결과 화면의 배경이 딴 방이 된다.
 */
export function createRevealPlayer(
  iso: IsoCamera,
  steps: readonly RevealStep[],
  onStep: RevealStepListener,
  onDone: () => void,
  options: RevealPlayerOptions = {},
): RevealPlayer {
  const secondsPerStep = options.secondsPerStep ?? TUNING.revealSecondsPerChange
  const durations = stepDurations(steps, secondsPerStep, options.budgetSeconds)
  const totalSeconds = durations.reduce((sum, value) => sum + value, 0)

  let index = -1
  let elapsed = 0
  let zoomFrom = iso.getZoom()
  let emitted: RevealView | null = null
  let finished = false
  let disposed = false

  function durationAt(at: number): number {
    return durations[at] ?? REVEAL_PACING.minStepSeconds
  }

  function stepAt(at: number): RevealStep | undefined {
    return steps[at]
  }

  function emit(view: RevealView): void {
    const step = stepAt(index)
    if (step === undefined) return
    emitted = view
    onStep(step, view, index)
  }

  /** 스텝 진입 - 카메라에 "어디를 보라"고 지시하고 화면을 변경본으로 되돌린다. */
  function enterStep(next: number): void {
    index = next
    const step = stepAt(next)
    if (step === undefined) return

    zoomFrom = iso.getZoom()
    iso.setDirection(step.direction, true)
    iso.focusOn(step.focusWorld, true)
    emit('mutated')
  }

  function applyZoom(step: RevealStep): void {
    const approach = Math.min(
      REVEAL_PACING.approachSeconds,
      durationAt(index) * REVEAL_PACING.approachMaxFraction,
    )
    // 접근 시간이 0이면 즉시 목표 줌으로 간다. 나누기 전에 걸러야 NaN이 카메라로 새어 나가지 않는다.
    const progress = approach <= 0 ? 1 : clamp01(elapsed / approach)
    iso.setZoom(zoomFrom + (step.zoom - zoomFrom) * smoothstep(progress))
  }

  function viewAt(duration: number): RevealView {
    const settle = Math.min(
      REVEAL_PACING.approachSeconds,
      duration * REVEAL_PACING.approachMaxFraction,
    )
    if (elapsed < settle) return 'mutated'

    const beat = Math.floor((elapsed - settle) / REVEAL_PACING.blinkIntervalSeconds)
    return beat % 2 === 0 ? 'original' : 'mutated'
  }

  function finish(): void {
    if (finished) return
    finished = true
    // 마지막 장이 원본이면 되돌려 놓는다(위 주석 참고).
    if (emitted === 'original') emit('mutated')
    onDone()
  }

  return {
    totalSeconds,

    update(dt: number): void {
      if (disposed || finished) return
      if (steps.length === 0) {
        finish()
        return
      }

      if (index < 0) enterStep(0)
      elapsed += Math.max(dt, 0)

      while (elapsed >= durationAt(index)) {
        elapsed -= durationAt(index)
        if (index + 1 >= steps.length) {
          finish()
          return
        }
        enterStep(index + 1)
      }

      const step = stepAt(index)
      if (step === undefined) return

      applyZoom(step)
      const view = viewAt(durationAt(index))
      if (view !== emitted) emit(view)
    },

    skip(): void {
      if (disposed) return
      finish()
    },

    dispose(): void {
      disposed = true
      finished = true
    },
  }
}

/**
 * 스텝별 길이(초).
 *
 * 발견분은 짧게 훑고, 상한(`budgetSeconds`)이 있으면 전체를 같은 비율로 줄인다.
 * 늘리지는 않는다 - 남는 시간은 결과 화면이 일찍 뜨는 것으로 쓰는 편이 낫다.
 * 컨셉시트 8절의 판정 지표가 "한 판 더"인 이상, 기다리게 만드는 초는 전부 손해다.
 */
function stepDurations(
  steps: readonly RevealStep[],
  secondsPerStep: number,
  budgetSeconds: number | undefined,
): readonly number[] {
  const natural = steps.map((step) =>
    Math.max(
      REVEAL_PACING.minStepSeconds,
      step.found ? secondsPerStep * REVEAL_PACING.foundStepScale : secondsPerStep,
    ),
  )

  const total = natural.reduce((sum, value) => sum + value, 0)
  if (budgetSeconds === undefined || total <= budgetSeconds || total <= 0) return natural

  const scale = Math.max(budgetSeconds, 0) / total
  return natural.map((value) => Math.max(REVEAL_PACING.minStepSeconds, value * scale))
}
